import { getData } from '../utils/ajax-utils';
import { scrollToElementIfNotVisible } from '../utils/utils';

export default function initContactSearch() : void {
    document.querySelectorAll<HTMLElement>('.js-contact-search').forEach($container => {
        new ContactSearch($container);
    });
}

type ContactType = {
    id: string,
    fullname: string,
    email: string,
};

export interface ContactHTMLElement extends HTMLElement {
    'data-id': string,
    'data-fullname': string,
    'data-email': string,
}

/**
 * Create a custom contact search input
 * 
 * Example:
 * <div class="js-contact-search">
 *      <input
 *          type="text"
 *          class="js-contact-search-input"
 *          data-thirdparty-id="40"
 *          data-contact-id-ignored="14" (optional, contact ID to ignore in search results)
 *          data-contacts="[{\"id\": 12, \"label\": \"John Doe - john@doe.com\"}]" (optional, default contacts list)
 *          data-fields="[\"id\", \"fullname\", \"email\"]" (optional, list of fields to fetch in results)
 *          data-label-template="{fullname} - {email}" (optional)
 *          data-remove-label="Delete" (optional)
 *          data-selected="12" (optional, the pre-selected contact)
 *      >
 *      <input type="hidden" class="js-contact-search-hidden-input"> (This input contains the selected contact ID)
 * </div>
 * 
 * @property {string}           thirdpartyId           The ThirdParty ID to search in
 * @property {string}           contactIdIgnored       The Contact ID to ignore in search results
 * @property {string}           fieldsToFetch          List of all fields to fetch in results (default: ['id'])
 * @property {string}           labelTemplate          The template to display in the dropdown list
 */
class ContactSearch {
    $container: HTMLElement;
    $hiddenInput: HTMLInputElement|null; // Hidden input containing the selected contact ID

    $searchInputWrapper: HTMLDivElement;
    $searchInput: HTMLInputElement;

    $selectedItemContainer: HTMLDivElement;
    $dropdown: HTMLDivElement; // Dropdown containing the search results

    thirdpartyId: string|false;
    contactIdIgnored: string|false;
    fieldsToFetch: (keyof ContactType)[];
    labelTemplate: string;
    removeLabel: string;

    defaultContactsList: ContactType[];
    contactsList: ContactType[];

    selectedContact: ContactType|ContactHTMLElement|null = null;

    searchTimeout: ReturnType<typeof setTimeout>|null = null;
    searchAbortController: AbortController|null = null;

    searchCache: { [key: string]: ContactType[] } = {};
    lastSearchValue = '';

    constructor($container: HTMLElement) {
        // Fetching all required elements
        this.$container = $container;
        const $searchInput = this.$container.querySelector<HTMLInputElement>('.js-contact-search-input');
        this.$hiddenInput = this.$container.querySelector<HTMLInputElement>('.js-contact-search-hidden-input');

        // Throw an error if one required elements doesn\'t exists
        if (!$searchInput) {
            throw new Error('The search input (.js-contact-search-input) doesn\'t exists.');
        } else if (!this.$hiddenInput) {
            throw new Error('The hidden input (.js-contact-search-hidden-input) doesn\'t exists.');
        }

        // Fetching data
        this.thirdpartyId = $searchInput.dataset.thirdpartyId || false;
        this.contactIdIgnored = $searchInput.dataset.contactIdIgnored || false;
        this.fieldsToFetch = JSON.parse($searchInput.dataset.fields || '["id"]');
        this.labelTemplate = $searchInput.dataset.labelTemplate || '{id}';
        this.removeLabel = $searchInput.dataset.removeLabel || 'Delete';
        const preSelected = $searchInput.dataset.selected || null;

        $searchInput.removeAttribute('data-thirdparty-id');
        $searchInput.removeAttribute('data-contact-id-ignored');
        $searchInput.removeAttribute('data-fields');
        $searchInput.removeAttribute('data-label-template');
        $searchInput.removeAttribute('data-remove-label');
        $searchInput.removeAttribute('data-selected');

        if (!this.fieldsToFetch.includes('id')) {
            this.fieldsToFetch.push('id');
        }

        // Throw an error if no thirdparty id is defined
        if (!this.thirdpartyId) {
            throw new Error('The search input must have an attribute \'data-thirdparty-id\'.');
        }

        // Wrap the search input
        this.$searchInputWrapper = document.createElement('div');
        this.$searchInputWrapper.classList.add('contact-search-input-wrapper');
        this.$searchInput = $searchInput.cloneNode(true) as HTMLInputElement;
        this.$searchInputWrapper.appendChild(this.$searchInput);
        ($searchInput.parentNode as HTMLElement).replaceChild(this.$searchInputWrapper, $searchInput);

        // Fetching the default contacts list
        this.defaultContactsList = JSON.parse(this.$searchInput.dataset.contacts || '[]');
        this.contactsList = this.defaultContactsList;
        this.$searchInput.removeAttribute('data-contacts');

        // Create the selected item container
        this.$selectedItemContainer = document.createElement('div');
        this.$selectedItemContainer.classList.add('contact-search-selected-item');

        // Create the dropdown element
        this.$dropdown = document.createElement('div');
        this.$dropdown.classList.add('contact-search-dropdown');

        // Populate the dropdown element with default contacts list
        this.populateDropdown();

        // Insert the selected item container and the dropdown element in DOM
        this.$searchInputWrapper.appendChild(this.$selectedItemContainer);
        this.$searchInputWrapper.appendChild(this.$dropdown);

        // Create the loader
        const $loader = document.createElement('div');
        $loader.classList.add('loader');
        this.$searchInputWrapper.appendChild($loader);

        // Bind the events
        if (!this.$searchInput.readOnly) {
            this.$searchInput.addEventListener('keyup', () => this.onSearchInputKeyup());
            this.$searchInput.addEventListener('keydown', evt => this.onSearchInputKeydown(evt));
            this.$dropdown.addEventListener('click', evt => this.onDropdownClick(evt));
            this.$dropdown.addEventListener('mouseover', evt => this.onDropdownMouseover(evt));
            this.$selectedItemContainer.addEventListener('click', evt => this.onSelectedItemClick(evt));
        }

        // If data-select is defined, preselect the contact defined inside
        if (preSelected) {
            this.selectItem(JSON.parse(preSelected));
        }
    }

    /**
     * Called when the event "keyup" is fired on the search input
     * 
     * @return {void}
     */
    onSearchInputKeyup() : void {
        const searchValue = this.$searchInput.value.trim();

        if (searchValue === this.lastSearchValue) {
            return;
        }

        // Wait 300ms to allow time to type the search request
        if (this.searchTimeout) {
            clearTimeout(this.searchTimeout);
        }

        this.searchTimeout = setTimeout(() => {
            this.$searchInputWrapper.classList.remove('wait');

            this.lastSearchValue = searchValue;
    
            // Abort previous request
            if (this.searchAbortController) {
                this.searchAbortController.abort();
            }
    
            // Populate default contacts list if the searchValue is empty
            if (searchValue.length < 3) {
                this.contactsList = this.defaultContactsList;
                this.populateDropdown();
                return;
            }
    
            // Search the result in the cache
            if (typeof this.searchCache[searchValue] !== 'undefined') {
                this.contactsList = this.searchCache[searchValue];
                this.populateDropdown();
                return;
            }
    
            // Show the loader
            this.$searchInputWrapper.classList.add('wait');
    
            const args = {
                action: 'get_contacts_list',
                thirdparty_id: this.thirdpartyId.toString(),
                ignore_contact_id: this.contactIdIgnored.toString(),
                search: searchValue,
                fields: this.fieldsToFetch.join(','),
                search_limit: '10',
                sort_field: 't.lastname',
            };
    
            this.searchAbortController = new AbortController();
            getData(scriptVar.ajaxUrl, args, this.searchAbortController.signal).then(response => {
                if (response.success) {
                    // Populate the results
                    this.searchCache[searchValue] = response.data.contacts;
                    this.contactsList = response.data.contacts;
                    this.populateDropdown();
                } else {
                    console.error(response.data.message);
                }
    
                this.$searchInputWrapper.classList.remove('wait');
            }).catch(() => {
                this.$searchInputWrapper.classList.remove('wait');
            });
        }, 300);
    }

    /**
     * Called when the event "keydown" is fired on the search input
     * 
     * @param {KeyboardEvent} evt 
     * 
     * @return {void}
     */
    onSearchInputKeydown(evt: KeyboardEvent) : void {
        // Do nothing if a contact is already selected
        if (this.selectedContact) {
            return;
        }

        if (['ArrowUp', 'ArrowDown'].includes(evt.key)) {
            // If the dropdown is empty, do nothing
            if (this.$dropdown.innerHTML === '') {
                return;
            }

            // Retrieve the active item
            const $activeItem = this.$dropdown.querySelector('.item.active') || null;

            // Retrieve the current active item index
            const currentItemIndex = $activeItem ? Array.from(this.$dropdown.children).indexOf($activeItem) + 1 : null;

            // Calculate the previous and next item index
            let prevItemIndex;
            let nextItemIndex;
            if (currentItemIndex) {
                prevItemIndex = (currentItemIndex - 1) <= 0 ? this.$dropdown.children.length : currentItemIndex - 1;
                nextItemIndex = (currentItemIndex + 1) > this.$dropdown.children.length ? 1 : currentItemIndex + 1;
            } else {
                prevItemIndex = this.$dropdown.children.length;
                nextItemIndex = 1;
            }

            // Retrieve the previous and next item
            const $prevItem = this.$dropdown.querySelector<HTMLElement>(`.item:nth-child(${prevItemIndex})`);
            const $nextItem = this.$dropdown.querySelector<HTMLElement>(`.item:nth-child(${nextItemIndex})`);

            if ($activeItem) {
                $activeItem.classList.remove('active');
            }

            switch (evt.key) {
                case 'ArrowUp':
                    if ($prevItem) {
                        $prevItem.classList.add('active');

                        scrollToElementIfNotVisible($prevItem, this.$dropdown);
                    }
                    break;
                case 'ArrowDown':
                    if ($nextItem) {
                        $nextItem.classList.add('active');

                        scrollToElementIfNotVisible($nextItem, this.$dropdown);
                    }
                    break;
            }
        } else if (evt.key === 'Enter') {
            const $activeItem = this.$dropdown.querySelector<ContactHTMLElement>('.item.active');

            evt.preventDefault();

            // If the dropdown is empty or no element is active, do nothing
            if (this.$dropdown.innerHTML === '' || !$activeItem) {
                return;
            }

            this.selectItem($activeItem);
        }
    }

    /**
     * Called when the event "click" is fired on the dropdown list
     * 
     * @param {MouseEvent} evt 
     * 
     * @return {void}
     */
    onDropdownClick(evt: MouseEvent) : void {
        // Do nothing if a contact is already selected
        if (this.selectedContact) {
            return;
        }

        evt.preventDefault();

        const $item = evt.target as ContactHTMLElement;

        if (!$item.classList.contains('item')) {
            return;
        }

        if ($item.classList.contains('selected')) {
            this.unselectItem();
        } else {
            this.selectItem($item);
        }
    }

    /**
     * Called when the event "mouseover" is fired on the dropdown list
     * 
     * @param {MouseEvent} evt 
     * 
     * @return {void}
     */
    onDropdownMouseover(evt: MouseEvent) : void {
        if (!(evt.target as HTMLElement).classList.contains('item')) {
            return;
        }

        const $activeItem = this.$dropdown.querySelector('.item.active');

        // Remove the active class on the active item
        if ($activeItem) {
            $activeItem.classList.remove('active');
        }
    }

    /**
     * Called when the event "click" is fired on the selected item container
     * 
     * @param {MouseEvent} evt 
     * 
     * @return {void}
     */
    onSelectedItemClick(evt: MouseEvent) : void {
        if (!(evt.target as HTMLElement).classList.contains('item')) {
            return;
        }

        this.unselectItem();
    }

    /**
     * Populate the dropdown with the current contacts list
     * 
     * @return {void}
     */
    populateDropdown() : void {
        // Clear the dropdown
        this.$dropdown.innerHTML = '';

        // Fill the dropdown with new contacts
        this.contactsList.forEach(contact => {
            const $dropdownLine = document.createElement('div');
            $dropdownLine.classList.add('item');
            $dropdownLine.dataset.id = contact.id;

            // Add the selected style if this contact is the selected one
            if (this.selectedContact && contact.id === this.selectedContact.id) {
                $dropdownLine.classList.add('selected');
            }

            // Create the label from the template defined
            let label = this.labelTemplate;
            this.fieldsToFetch.forEach(fieldName => {
                label = label.replace(`{${fieldName}}`, contact[fieldName]);
            });
            $dropdownLine.innerText = label;

            this.$dropdown.appendChild($dropdownLine);
        });
    }

    /**
     * Select an item in the dropdown
     * 
     * @param {ContactHTMLElement} $item 
     * 
     * @return {void}
     */
    selectItem($item: ContactHTMLElement) : void {
        // Find the previous selected item into the dropdown and unselect it if exists
        const $previousSelectedItem = this.$dropdown.querySelector('.selected');
        if ($previousSelectedItem) {
            $previousSelectedItem.classList.remove('selected');
            this.$selectedItemContainer.innerHTML = '';
        }

        if (typeof $item.dataset === 'undefined') { // $item is not a DOM element
            // Search the contact inside the selectedContact array
            this.selectedContact = this.contactsList.filter(contact => contact.id === $item.id)[0] || null;

            // If the contact exists in the selectedContact array, retrieve the HTMLElement in the dropdown
            if (this.selectedContact) {
                $item = this.$dropdown.querySelector(`.item[data-id="${this.selectedContact.id}"]`) as ContactHTMLElement;
            }
        }

        let $selectedItem: HTMLElement;

        if (typeof $item.dataset === 'undefined') { // The selected contact is not in the dropdown
            this.selectedContact = $item;

            // Create the item
            $selectedItem = document.createElement('div');
            $selectedItem.classList.add('item', 'selected');
            $selectedItem.dataset.id = $item.id;

            // Create the label from the template defined
            let label = this.labelTemplate;
            this.fieldsToFetch.forEach(fieldName => {
                // @ts-expect-error fieldName contains only authorized string but typescript do not recognized it
                label = label.replace(`{${fieldName}}`, $item[fieldName]);
            });
            $selectedItem.innerText = label;
        } else {
            this.selectedContact = this.contactsList.filter(contact => contact.id.toString() === $item.dataset.id)[0];

            // Add the selected item styles
            $item.classList.add('selected');

            // Clone the selected item to insert it into the selected item container
            $selectedItem = $item.cloneNode(true) as HTMLElement;
        }

        // Insert the selected item into the selected item container
        $selectedItem.setAttribute('title', this.removeLabel);
        this.$selectedItemContainer.appendChild($selectedItem);

        // Set the hidden input value
        if (this.$hiddenInput) {
            this.$hiddenInput.value = JSON.stringify(this.selectedContact);
        }

        // Clear the dropdown
        this.$dropdown.innerHTML = '';
    }

    /**
     * Unselect the selected item
     * 
     * @return {void}
     */
    unselectItem() : void {
        this.selectedContact = null;

        // Clear the selected item container
        this.$selectedItemContainer.innerHTML = '';

        // Find the selected item into the dropdown and unselect it if exists
        const $selectedItem = this.$dropdown.querySelector('.selected');
        if ($selectedItem) {
            $selectedItem.classList.remove('selected');
        }

        // Clear the hiden input value
        if (this.$hiddenInput) {
            this.$hiddenInput.value = '';
        }

        // Populate the dropdown
        this.populateDropdown();

        // Focus the search input
        this.$searchInput.focus();
    }
}
